import { useEffect, useLayoutEffect } from "react";
import {
  BrowserRouter,
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import PrivacyPolitycy from "./features/PrivacyPolitycy";
import SerceFinansow from "./features/SerceFinansow";
import Kontakt from "./common/Kontakt";
import Menu from "./common/Menu";
import Regulamin from "./features/Regulamin";
import KalkulatorNadplat from "./features/KalkulatorNadplat";
import KalkulatorRataWklad from "./features/KalkulatorRataWklad";
import ServicePage from "./features/ServicePage";
import DecisionGuides, { DecisionGuide, guides } from "./features/DecisionGuides";
import { serviceData, servicePages } from "./features/ServicePage/serviceData";
import ThankYou from "./features/ThankYou";
import NotFound from "./features/NotFound";
import MobileContactBar from "./common/MobileContactBar";
import Checklisty from "./features/Checklisty";
import { getLeadAttribution, pushDataLayer } from "./analytics";

const SITE_URL = "https://sercefinansow.pl";

const pageMetadata = {
  "/ubezpieczenie-rodziny": { title: "Ubezpieczenie rodziny – zakres ochrony | Serce Finansów", description: "Ochrona życia i umowy dodatkowe dopasowane do potrzeb rodziny. Zakres, suma, wyłączenia oraz przygotowanie do rozmowy." },
  "/leasing-dla-firmy": { title: "Leasing samochodu, sprzętu i maszyn | Serce Finansów", description: "Porównaj wpłatę, raty, wykup i dodatkowe koszty leasingu. Zaplanuj finansowanie samochodu lub wyposażenia firmy." },
  "/przed-decyzja": { title: "Przed decyzją – praktyczne wskazówki | Serce Finansów", description: "Co sprawdzić przed zakupem, budową lub zmianą kredytu? Bezpłatne odpowiedzi, przykłady i narzędzia do kolejnego kroku." },
  "/rodzinny-kredyt-mieszkaniowy": {
    title: "Rodzinny Kredyt Mieszkaniowy i wkład BGK | Serce Finansów",
    description: "Jak działa RKM, gwarancja BGK i wkład własny? Zasady programu, przykład ceny 600 000 zł i dokumenty do przygotowania.",
  },
  "/pozyczka-hipoteczna": {
    title: "Pożyczka hipoteczna – zasady i dokumenty | Serce Finansów",
    description: "Czym pożyczka hipoteczna różni się od kredytu mieszkaniowego? Cel, zabezpieczenie nieruchomością, koszty i dokumenty.",
  },
  "/": {
    title: "Pośrednik kredytowy Suwałki | Serce Finansów",
    description:
      "Bezpłatna pomoc w wyborze kredytu w Suwałkach: zakup mieszkania, budowa domu i refinansowanie. Justyna i Paweł Kowalewscy. Umów rozmowę.",
  },
  "/kalkulator-nadplat": {
    title: "Kalkulator nadpłaty kredytu hipotecznego | Serce Finansów",
    description:
      "Oblicz orientacyjne oszczędności z nadpłaty kredytu hipotecznego i porównaj skrócenie okresu z obniżeniem raty.",
  },
  "/kalkulator-rata-wklad": {
    title: "Kalkulator raty i wkładu własnego | Serce Finansów",
    description:
      "Porównaj orientacyjną ratę i wkład własny dla wariantów 10%, 20% oraz Rodzinnego Kredytu Mieszkaniowego.",
  },
  [serviceData.hipoteka.path]: {
    title: "Kredyt hipoteczny Suwałki | Serce Finansów",
    description: "Kredyt hipoteczny w Suwałkach i online w całej Polsce. Porównanie możliwości, dokumenty nieruchomości i prowadzenie procesu do uruchomienia.",
  },
  [serviceData.budowa.path]: {
    title: "Kredyt na budowę domu Suwałki | Serce Finansów",
    description: "Finansowanie budowy domu w Suwałkach. Działka jako wkład, kosztorys, harmonogram, transze i dokumenty wymagane przez bank.",
  },
  [serviceData.deweloper.path]: {
    title: "Kredyt na mieszkanie od dewelopera Suwałki | Serce Finansów",
    description: "Finansowanie mieszkania z rynku pierwotnego w Suwałkach. Umowa, harmonogram płatności, dokumenty inwestycji i wypłata kredytu.",
  },
  [serviceData.refinansowanie.path]: {
    title: "Refinansowanie kredytu hipotecznego Suwałki | Serce Finansów",
    description: "Sprawdź refinansowanie kredytu hipotecznego w Suwałkach. Porównaj ratę, okres, odsetki i pełne koszty przeniesienia kredytu.",
  },
  [serviceData.gotowkowy.path]: {
    title: "Kredyt gotówkowy Suwałki | Serce Finansów",
    description: "Porównanie kredytów gotówkowych w Suwałkach. Rata, oprocentowanie, prowizja, ubezpieczenie i koszt całkowity.",
  },
  [serviceData.firma.path]: {
    title: "Finansowanie firmy i leasing Suwałki | Serce Finansów",
    description: "Kredyty firmowe, leasing i finansowanie inwestycji w Suwałkach. Porównanie rozwiązań dopasowanych do celu oraz wyników firmy.",
  },
  "/dziekujemy": {
    title: "Dziękujemy za kontakt | Serce Finansów",
    description: "Potwierdzenie wysłania formularza kontaktowego.",
    noindex: true,
  },
  "/checklisty": {
    title: "Checklisty kredytowe i dokumenty | Serce Finansów",
    description: "Bezpłatne checklisty dokumentów do zakupu nieruchomości, kredytu na budowę domu i refinansowania.",
  },
  "/polityka-prywatności": {
    title: "Polityka prywatności | Serce Finansów",
    description: "Zasady przetwarzania danych osobowych w Serce Finansów.",
    noindex: true,
  },
  "/regulamin": {
    title: "Regulamin strony | Serce Finansów",
    description: "Regulamin korzystania ze strony internetowej Serce Finansów.",
    noindex: true,
  },
};

const setMetaContent = (selector, attribute, value) => {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
};

const PageMetadata = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const canonicalPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const guide = guides.find(item => `/przed-decyzja/${item.slug}` === canonicalPath);
    const service = servicePages.find(item => item.path === canonicalPath);
    const metadata = pageMetadata[canonicalPath] || (guide ? { title: `${guide.title} | Serce Finansów`, description: guide.description } : service ? { title: `${service.title} | Serce Finansów`, description: service.lead } : {
      title: "Nie znaleziono strony | Serce Finansów",
      description: "Nie znaleziono wskazanej strony.",
      noindex: true,
    });
    const previewHost = window.location.hostname.endsWith(".chatgpt.site") || window.location.hostname.endsWith(".netlify.app");

    document.title = metadata.title;
    setMetaContent('meta[name="description"]', "content", metadata.description);
    setMetaContent('meta[name="robots"]', "content", previewHost ? "noindex,nofollow" : metadata.noindex ? "noindex,follow" : "index,follow");
    setMetaContent('meta[property="og:title"]', "content", metadata.title);
    setMetaContent('meta[property="og:description"]', "content", metadata.description);
    setMetaContent('meta[property="og:url"]', "content", `${SITE_URL}${canonicalPath}`);

    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", `${SITE_URL}${canonicalPath}`);
    }
  }, [pathname]);

  return null;
};

const LegacyHashRedirect = () => {
  const history = useHistory();

  useLayoutEffect(() => {
    const legacyPath = window.location.hash.replace(/^#/, "");
    if (legacyPath.startsWith("/")) {
      history.replace(legacyPath);
    }
  }, [history]);

  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    const timer = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      root.style.scrollBehavior = previousScrollBehavior;
    }, 40);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      root.style.scrollBehavior = previousScrollBehavior;
    };
  }, [pathname]);

  return null;
};

const ConversionTracking = () => {
  useEffect(() => {
    getLeadAttribution();

    const trackClick = (event) => {
      const link = event.target.closest("a");
      if (!link) return;
      const href = link.getAttribute("href") || "";

      if (link.dataset.serviceChoice !== undefined) {
        pushDataLayer("sf_service_choice_click", { service_id: link.dataset.serviceChoice, destination: href });
      }
      if (link.dataset.contactCta !== undefined) {
        pushDataLayer("sf_contact_cta_click", {
          label: link.textContent.trim().replace(/\s+/g, " ").slice(0, 100),
          destination: href,
        });
      }
      if (href.includes("source=kalkulator")) {
        pushDataLayer("sf_calculator_to_contact", {
          source: new URL(href, window.location.origin).searchParams.get("source"),
        });
      }
      if (href.startsWith("tel:")) pushDataLayer("sf_phone_click", { location: "website", person: link.dataset.phonePerson || "unknown" });
      if (href.startsWith("mailto:")) pushDataLayer("sf_email_click", { location: "website" });
      if (link.dataset.reviewsLink !== undefined) pushDataLayer("sf_google_reviews_click", { location: "website" });
      else if (link.dataset.mapLink !== undefined || href.includes("google.com/maps") || href.includes("maps.google.com")) pushDataLayer("sf_map_click", { location: "contact" });
      if (href.startsWith("/narzedzia") || href.startsWith("/kalkulator")) {
        pushDataLayer("sf_tool_open", { tool_path: href });
      }
    };

    document.addEventListener("click", trackClick);
    return () => document.removeEventListener("click", trackClick);
  }, []);

  return null;
};

function App() {
  return (
    <BrowserRouter>
      <LegacyHashRedirect />
      <ScrollToTop />
      <PageMetadata />
      <ConversionTracking />
      <Menu />

      <Switch>
        <Route path="/polityka-prywatności">
          <PrivacyPolitycy />
        </Route>
        <Route path="/regulamin">
          <Regulamin />
        </Route>
        <Route path="/kalkulator-nadplat">
          <KalkulatorNadplat />
        </Route>
        <Route path="/kalkulator-rata-wklad">
          <KalkulatorRataWklad />
        </Route>
        {servicePages.map((service) => (
          <Route exact path={service.path} key={service.path}>
            <ServicePage service={service} />
          </Route>
        ))}
        <Route exact path="/przed-decyzja"><DecisionGuides /></Route>
        {guides.map(guide => <Route exact path={`/przed-decyzja/${guide.slug}`} key={guide.slug}><DecisionGuide guide={guide} /></Route>)}
        <Route exact path="/dziekujemy">
          <ThankYou />
        </Route>
        <Route exact path="/checklisty">
          <Checklisty />
        </Route>
        <Route exact path="/formularz">
          <Redirect to="/?source=formularz" />
        </Route>
        <Route exact path="/">
          <SerceFinansow />
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>

      <Kontakt />
      <MobileContactBar />
    </BrowserRouter>
  );
}

export default App;

